// src/components/ChatModal.js
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, Box, Paper, Avatar
} from '@mui/material';
import { format } from 'date-fns';

// Helper: convert absolute URL → relative media path
function toRelativeMedia(url) {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/media/")) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url.replace(/^https?:\/\/[^/]+/, "");
  }
  const trimmed = url.replace(/^\.?\/*/, "");
  return trimmed.startsWith("media/") ? `/${trimmed}` : `/media/${trimmed}`;
}

export default function ChatModal({
  open,
  onClose,
  conversation,
  messages,
  onSend,
  newMsg,
  setNewMsg,
  authUserId,
  messagesEndRef,
  recipient
}) {
  if (!conversation) return null;

  console.log('ChatModal recipient:', recipient);

  // Recipient name
  const otherName = recipient?.first_name
    ? `${recipient.first_name} ${recipient.last_name}`
    : recipient?.display_name || recipient?.username || "User";

  // Recipient primary profile image — may come from:
  // recipient.primary_profile_image (preferred)
  // recipient.profile_images[] (fallback)
  const primaryProfileImage =
    recipient?.primary_profile_image
      ? toRelativeMedia(recipient.primary_profile_image)
      : (
          Array.isArray(recipient?.profile_images)
            ? toRelativeMedia(
                recipient.profile_images.find((p) => p.is_primary)?.url ||
                recipient.profile_images[0]?.url
              )
            : null
        );
    console.log('ChatModal primaryProfileImage:', primaryProfileImage);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      
      {/* TITLE WITH PROFILE IMAGE */}
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5
          }}
        >
          {primaryProfileImage ? (
            <Avatar
              src={primaryProfileImage}
              alt={otherName}
              sx={{ width: 40, height: 40 }}
              imgProps={{ referrerPolicy: "no-referrer" }}
            />
          ) : (
            <Avatar sx={{ width: 40, height: 40 }}>
              {otherName.charAt(0)}
            </Avatar>
          )}

          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Chat with {otherName}
          </Typography>
        </Box>
      </DialogTitle>

      {/* MESSAGE LIST */}
      <DialogContent>
        <Box sx={{ maxHeight: "400px", overflowY: "auto", mb: 2 }}>
          {messages.map((msg) => {
            const isMe = msg.sender.id === authUserId;

            return (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  justifyContent: isMe ? "flex-end" : "flex-start",
                  mb: 1
                }}
              >
                <Paper
                  sx={{
                    p: 1.5,
                    bgcolor: isMe ? "#cfe8fc" : "#f5f5f5",
                    maxWidth: "60%",
                    borderRadius: 2,
                    position: "relative",
                    borderTopLeftRadius: isMe ? 16 : 4,
                    borderTopRightRadius: isMe ? 4 : 16,
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 12,
                      width: 0,
                      height: 0,
                      border: "8px solid transparent",
                      borderRight: isMe ? "8px solid #cfe8fc" : "none",
                      borderLeft: !isMe ? "8px solid #f5f5f5" : "none",
                      right: isMe ? -8 : "auto",
                      left: !isMe ? -8 : "auto"
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {isMe ? "You" : otherName}
                  </Typography>
                  <Typography variant="body1">{msg.content}</Typography>

                  {msg.timestamp && (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.5, color: "gray" }}
                    >
                      {format(new Date(msg.timestamp), "PPpp")}
                    </Typography>
                  )}
                </Paper>
              </Box>
            );
          })}

          <div ref={messagesEndRef} />
        </Box>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={newMsg}
          onChange={(e) => setNewMsg(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSend()}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onSend}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
